import React, {
    useContext, useCallback, useEffect
} from 'react';
import _ from 'lodash';
import { WizardPage, wizardProps, WizardPageTemplate } from 'gw-portals-wizard-react';
import { useDependencies } from 'gw-portals-dependency-react';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { TranslatorContext } from '@jutro/locale';
import {
    ModalNextProvider
} from '@jutro/components';
import { error } from '@jutro/logger';
import { useValidation } from 'gw-portals-validation-react';
import { messages as commonMessages } from 'gw-platform-translations';
import { readViewModelValue } from 'gw-jutro-adapters-react';
import { DateUtil } from 'gw-portals-util-js';
import { ViewModelForm, withViewModelService, ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import {
    withApplicationPropertyContext
} from 'tdic-components-platform-react';
import metadata from './TypeOfIncidentPage.metadata.json5';
import styles from './TypeOfIncidentPage.module.scss';
import Claim from '../../../models/Claim';
import fnolCommonMessages from '../../../FNOL.messages';
import './TypeOfIncident.messages';

function FNOLBOPTypeOfIncidentPage(props) {
    const {
        wizardData: claimVM,
        updateWizardData,
        authHeader,
        history: {
            location: { state = {} }
        },
        loggedUserContact
    } = props;
    const translator = useContext(TranslatorContext);
    const viewModelService = useContext(ViewModelServiceContext);
    const { FNOLService } = useDependencies('FNOLService');
    const { policyType } = _.get(claimVM.value, 'policy.policyType');
    const lobPolicyType = _.lowerFirst(policyType);
    const lobExtension = claimVM.lobs[lobPolicyType];
    if (_.isEmpty(claimVM.generalLob)) {
        _.set(claimVM, 'generalLob', lobExtension);
    }
    const {
        onValidate,
        isComponentValid,
        initialValidation,
        registerInitialComponentValidation,
        registerComponentValidation
    } = useValidation('FNOLTypeOfIncidentPage');

    const initializeVM = useCallback(() => {
        const submittingClaimVM = viewModelService.create(
            new Claim(claimVM.value),
            'cc',
            'com.tdic.edge.capabilities.claim.fnol.dto.FnolDTO_Ext',
            {
                ReportedDetails: true,
                TypeOfIncidentReporter: true
            }
        );
        const newClaimVM = viewModelService.clone(submittingClaimVM);
        updateWizardData(newClaimVM);
        return newClaimVM;
    }, [claimVM, updateWizardData, viewModelService]);

    const accountDetails = useCallback(async () => {
        const newClaimVM = initializeVM();
        const reporter = {
            taxID: null,
            lastName: _.get(loggedUserContact, 'accountContact.lastName', ''),
            firstName: _.get(loggedUserContact, 'accountContact.firstName', ''),
            adanumber: _.get(loggedUserContact, 'accountContact.adaNumber_Ext', '')
        };
        _.set(newClaimVM, 'reporter', reporter);
        if (newClaimVM.value.typeOfClaim_Ext !== 'cyberliability') {
            _.set(newClaimVM.value, 'coverageType_Ext', null);
        }
        updateWizardData(newClaimVM);
    }, [initializeVM, loggedUserContact, updateWizardData]);

    useEffect(
        () => {
            accountDetails();
        },
        // Call once when page is mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const checkDateWithinRange = useCallback(() => {
        const dateOfNotice = _.get(claimVM.value, 'lobs.businessOwners.dateOfNotice_Ext');
        if (!_.isEmpty(dateOfNotice)) {
            const lossDate = _.get(claimVM.value, 'lossDate');
            const reportedDate = moment().format();
            const dateStatus = DateUtil.compareIgnoreTime(dateOfNotice,
                lossDate);
            const changeDateStatus = DateUtil.compareIgnoreTime(dateOfNotice,
                reportedDate);
            return !((dateStatus === 1 || dateStatus === 0)
            && (changeDateStatus === -1 || changeDateStatus === 0));
        }
        return false;
    }, [claimVM]);

    const getNameValue = useCallback(() => {
        return _.get(loggedUserContact, 'accountContact.displayName', '');
    }, [loggedUserContact]);

    const handleSaveClaimData = useCallback(() => {
        return FNOLService.saveFNOLDetails(claimVM.value, authHeader)
            .then((response) => {
                const reportedDetails = _.get(claimVM.value, 'reporter');
                claimVM.value = response;
                _.set(claimVM.value, 'reporter', reportedDetails);
                return claimVM;
            })
            .catch((err) => {
                error(`TypeOfIncidentPage: handleSaveClaimData - Unable to handle save claim data ${JSON.stringify(err)}`);
                ModalNextProvider.showAlert({
                    title: fnolCommonMessages.fnolSaveDraftErrorTitle,
                    message: fnolCommonMessages.fnolSaveDraftErrorMessage,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok
                }).catch(_.noop);
                return false;
            });
    }, [FNOLService, authHeader, claimVM]);

    const onNext = useCallback(() => {
        const policyNumber = _.get(claimVM.value, 'policy.policyNumber');
        const isClaimNumberAvailable = _.get(claimVM.value, 'claimNumber');
        if (isClaimNumberAvailable) {
            return handleSaveClaimData();
        }
        return FNOLService.getFNOLDetails(policyNumber, claimVM.value, authHeader)
            .then((response) => {
                const reportedDetails = _.get(claimVM.value, 'reporter');
                claimVM.value = new Claim(response);
                _.set(claimVM.value, 'reporter', reportedDetails);
                return claimVM;
            })
            .catch((err) => {
                error(`TypeOfIncidentPage: onNext - Unable to create draft claim ${JSON.stringify(err)}`);
                ModalNextProvider.showAlert({
                    title: fnolCommonMessages.generalCreateDraftErrorTitle,
                    message: fnolCommonMessages.generalCreateDraftErrorMessage,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok
                }).catch(_.noop);
                return false;
            });
    }, [FNOLService, authHeader, claimVM, handleSaveClaimData]);

    const resolvers = {
        resolveClassNameMap: styles,
    };

    const overrideProps = {
        '@field': {
            showOptional: true
        },
        incidentName: {
            value: getNameValue()
        },
        dateOfNotice: {
            showErrors: checkDateWithinRange(),
            validationMessages: checkDateWithinRange()
                ? [translator(fnolCommonMessages.invalidDateOfNotice)] : []
        },
        relationToInsured: {
            value: _.get(claimVM.value, 'lobs.businessOwners.claimReporter_Ext.relationToInsured_Ext')
        },
        listOfDamaged: {
            required: true
        }
    };

    const readValue = useCallback(
        (id, path) => {
            return readViewModelValue(metadata.pageContent, claimVM, id, path, overrideProps);
        },
        [claimVM, overrideProps]
    );

    const { claimStatus } = state;

    const validateClaimStatus = useCallback(() => {
        return (
            !_.isNil(claimStatus)
            && claimStatus === translator({ id: 'typekey.ClaimState.draft', defaultMessage: 'Draft' })
        );
    }, [claimStatus, translator]);

    useEffect(() => {
        registerInitialComponentValidation(validateClaimStatus);
    }, [validateClaimStatus, registerInitialComponentValidation]);

    useEffect(() => {
        registerComponentValidation(() => {
            return claimVM.aspects.valid && claimVM.aspects.subtreeValid;
        });
    }, [claimVM, registerComponentValidation]);

    return (
        <WizardPage
            disableNext={!isComponentValid}
            skipWhen={initialValidation}
            onNext={onNext}
            template={WizardPageTemplate}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                resolveValue={readValue}
                classNameMap={resolvers.resolveClassNameMap}
            />
        </WizardPage>
    );
}

FNOLBOPTypeOfIncidentPage.propTypes = wizardProps;
export default withRouter(
    withAuthenticationContext(
        withViewModelService(
            withApplicationPropertyContext(
                FNOLBOPTypeOfIncidentPage
            )
        )
    )
);
